import React, { useState, useEffect } from 'react';
import {
  Form,
  Input,
  Button,
  DatePicker,
  Select,
  Row,
  Col,
  message,
  Divider,
  Typography,
} from 'antd';
import { SaveOutlined, CloseOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { Patient, PatientFormData } from '../../types/patient';
import { patientService } from '../../services/patientService';

const { Title } = Typography;
const { TextArea } = Input;
const { Option } = Select;

interface PatientFormProps {
  patient?: Patient | null;
  onSave: () => void;
  onCancel: () => void;
}

const PatientForm: React.FC<PatientFormProps> = ({ patient, onSave, onCancel }) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (patient) {
      form.setFieldsValue({
        ...patient,
        date_of_birth: patient.date_of_birth ? dayjs(patient.date_of_birth) : null,
      });
    } else {
      form.resetFields();
    }
  }, [patient, form]);

  const handleSubmit = async (values: any) => {
    setLoading(true);
    try {
      const formData: PatientFormData = {
        ...values,
        date_of_birth: values.date_of_birth ? values.date_of_birth.format('YYYY-MM-DD') : '',
      };

      if (patient) {
        await patientService.updatePatient(patient.id, formData);
        message.success('Patient updated successfully');
      } else {
        await patientService.createPatient(formData);
        message.success('Patient created successfully');
      }
      
      onSave();
    } catch (error) {
      message.error(patient ? 'Failed to update patient' : 'Failed to create patient');
      console.error('Error saving patient:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-h-[70vh] overflow-y-auto">
      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
        className="space-y-4"
      >
        {/* Basic Information */}
        <div>
          <Title level={4} className="!mb-4">Basic Information</Title>
          <Row gutter={16}>
            <Col xs={24} sm={12}>
              <Form.Item
                name="name"
                label="Full Name"
                rules={[{ required: true, message: 'Please enter patient name' }]}
              >
                <Input placeholder="Enter patient full name" />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item
                name="date_of_birth"
                label="Date of Birth"
              >
                <DatePicker
                  className="w-full"
                  placeholder="Select date of birth"
                  disabledDate={(current) => current && current > dayjs().endOf('day')}
                />
              </Form.Item>
            </Col>
          </Row>
        </div>

        <Divider />

        {/* Contact Information */}
        <div>
          <Title level={4} className="!mb-4">Contact Information</Title>
          <Row gutter={16}>
            <Col xs={24} sm={12}>
              <Form.Item
                name="email"
                label="Email"
                rules={[
                  { type: 'email', message: 'Please enter a valid email' }
                ]}
              >
                <Input placeholder="Enter email address" />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item name="phone" label="Phone Number">
                <Input placeholder="Enter phone number" />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item name="address" label="Address">
            <TextArea
              rows={3}
              placeholder="Enter full address"
            />
          </Form.Item>
        </div>

        <Divider />

        {/* Medical Information */}
        <div>
          <Title level={4} className="!mb-4">Medical Information</Title>
          <Form.Item name="medical_record_number" label="Medical Record Number">
            <Input placeholder="Enter medical record number" />
          </Form.Item>
        </div>

        <Divider />

        {/* Insurance Information */}
        <div>
          <Title level={4} className="!mb-4">Insurance Information</Title>
          <Form.Item name="insurance_info" label="Insurance Information">
            <TextArea
              rows={3}
              placeholder="Enter insurance provider and policy details (e.g., Blue Cross Blue Shield - Policy #ABC123456)"
            />
          </Form.Item>
        </div>

        <Divider />

        {/* Additional Notes */}
        <div>
          <Title level={4} className="!mb-4">Additional Notes</Title>
          <Form.Item name="notes" label="Notes">
            <TextArea
              rows={4}
              placeholder="Enter any additional notes about the patient"
            />
          </Form.Item>
        </div>

        {/* Form Actions */}
        <div className="flex justify-end space-x-3 pt-4 border-t">
          <Button
            onClick={onCancel}
            icon={<CloseOutlined />}
            disabled={loading}
          >
            Cancel
          </Button>
          <Button
            type="primary"
            htmlType="submit"
            icon={<SaveOutlined />}
            loading={loading}
          >
            {patient ? 'Update Patient' : 'Create Patient'}
          </Button>
        </div>
      </Form>
    </div>
  );
};

export default PatientForm; 